<script setup>
import BootstrapModal from "@/Components/BootstrapModal.vue";
import CardTitle from "@/Components/Links/CardTitle.vue";
import ShopButton from "@/Components/Links/Platforms/Shop/ShopButton.vue";
import ShareProductPopup from "@/Components/ShareProductPopup.vue";
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import SharePopup from "@/Components/SharePopup.vue";

const props = defineProps({
    getCollectionsWithProducts: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    appearance: { type: Object, default: () => ({}) },
    buttonStyle: { type: [Object, Array, String], default: () => ({}) },
    DoBucket: String,
    DoEndpoint: String,
    urlName: String,
    user: Object,
});

const emit = defineEmits(["share"]);

// Filter only active (status == 1) collections and products
const filteredCollections = computed(() => {
    const cols = props.getCollectionsWithProducts || [];
    return cols
        .filter((c) => String(c?.status) === "1" || c?.status === 1)
        .map((c) => ({
            ...c,
            products: (c.products || []).filter(
                (p) => String(p?.status) === "1" || p?.status === 1
            ),
        }));
});

const filteredProducts = computed(() =>
    (props.products || []).filter(
        (p) => String(p?.status) === "1" || p?.status === 1
    )
);

function toLinkLike(id, title, short_code = null, type = null) {
    return { id, title, platform: "shop", short_code, type };
}

// Modal state
const modalRef = ref(null);
const selectedCollection = ref(null);
const modalId = computed(
    () => `shop-collection-modal-${selectedCollection.value?.id ?? "shop"}`
);

// Infinite load-more state for modal products
const STEP = 1;
const showCount = ref(STEP);
const sentinelRef = ref(null);
let io = null;

// Share product popup state
const sharePopupVisible = ref(false);
const shareProd = ref(null);

function productShareUrl(prod) {
    return prod?.url || prod?.link || prod?.href || "";
}
function openShareForProduct(prod) {
    shareProd.value = prod;
    sharePopupVisible.value = true;
}
function closeSharePopup() {
    sharePopupVisible.value = false;
    shareProd.value = null;
}

const visibleProducts = computed(() =>
    (selectedCollection.value?.products || []).slice(0, showCount.value)
);
const totalProducts = computed(
    () => selectedCollection.value?.products?.length || 0
);
const hasMore = computed(() => showCount.value < totalProducts.value);

function setupObserver() {
    cleanupObserver();
    // Only observe when more items exist
    if (!hasMore.value) return;
    // Wait for DOM to render sentinel
    nextTick(() => {
        if (!sentinelRef.value) return;
        io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        loadMore();
                    }
                }
            },
            {
                root:
                    document.querySelector(`#${modalId.value} .modal-body`) ||
                    null,
                rootMargin: "0px 0px 200px 0px",
            }
        );
        io.observe(sentinelRef.value);
    });
}
function cleanupObserver() {
    if (io) {
        io.disconnect();
        io = null;
    }
}

async function openCollectionModal(collection) {
    if (!collection) return;
    selectedCollection.value = collection;
    showCount.value = STEP;
    await nextTick();
    setupObserver();
    modalRef.value?.showModal(modalId.value);
}

// Open a popup that shows ALL standalone products by reusing the collection modal
async function openProductsModal() {
    const prods = filteredProducts.value || [];
    if (!prods.length) return;
    selectedCollection.value = {
        id: "products",
        title: "Products",
        products: prods,
    };
    showCount.value = STEP;
    await nextTick();
    setupObserver();
    modalRef.value?.showModal(modalId.value);
}

watch(selectedCollection, async () => {
    // Reset count when selection changes/clears
    showCount.value = STEP;
    await nextTick();
    setupObserver();
});

function loadMore() {
    if (!hasMore.value) return;
    showCount.value = Math.min(showCount.value + STEP, totalProducts.value);
}

function onProductPreviewClick(collection, e) {
    e?.stopPropagation?.();
    openCollectionModal(collection);
}

function getProductImageSrc(p) {
    if (!p.file) return "";

    if (p.file.startsWith("demo_")) {
        return `${window.location.origin}/storage/${p.file}`;
    }

    return `${props.DoEndpoint}/${props.DoBucket}/products/${p.file}`;
}

function onShare(linkLike) {
    emit("share", linkLike);
}

onBeforeUnmount(() => cleanupObserver());
</script>

<template>
    <div class="shop-card-stack">
        <!-- Collections (preview) -->
        <ShopButton
            v-for="collection in filteredCollections"
            :key="`col-${collection.id}`"
            :link="
                toLinkLike(
                    collection.id,
                    collection.title,
                    collection.short_code,
                    'collection'
                )
            "
            :appearance="appearance"
            :buttonStyle="buttonStyle"
            :DoBucket="DoBucket"
            :DoEndpoint="DoEndpoint"
            :hideLeftImage="true"
            class="shop-card"
            @click="openCollectionModal(collection)"
            @share="onShare"
        >
            <template #title>
                <div
                    class="w-100 text-center d-flex flex-column align-items-center"
                >
                    <CardTitle
                        :title="collection.title || ''"
                        layout="classic"
                        align="center"
                        :max="100"
                    />
                    <small class=""
                        >{{ collection.products?.length || 0 }} products</small
                    >
                </div>
            </template>

            <!-- PREVIEW: ONLY 2 ITEMS -->
            <div
                v-if="collection.products?.length"
                class="collection-grid two-only"
            >
                <div
                    v-for="p in collection.products.slice(0, 2)"
                    :key="`p-${p.id}`"
                    class="grid-item"
                    role="button"
                    @click.stop="onProductPreviewClick(collection, $event)"
                    :class="appearance.buttons"
                >
                    <div class="image-wrap">
                        <img
                            v-if="p.file"
                            :src="getProductImageSrc(p)"
                            :alt="p.title || 'Product image'"
                            loading="lazy"
                        />
                        <div v-else class="no-image-fill"></div>

                        <div class="overlay">
                            <div class="overlay-title">{{ p.title }}</div>
                            <div v-if="p.price" class="price-pill">
                                {{ p.price
                                }}<span v-if="p.currency">
                                    {{ p.currency }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ShopButton>

        <!-- Standalone products grouped in a single card -->
        <ShopButton
            v-if="filteredProducts?.length"
            :key="`prod-group`"
            :link="toLinkLike('products', 'Products', null, 'products')"
            :appearance="appearance"
            :buttonStyle="buttonStyle"
            :DoBucket="DoBucket"
            :DoEndpoint="DoEndpoint"
            :hideLeftImage="true"
            class="shop-card"
            @click="openProductsModal"
            @share="onShare"
        >
            <template #title>
                <div
                    class="w-100 text-center d-flex flex-column align-items-center"
                >
                    <CardTitle
                        :title="'Products'"
                        layout="classic"
                        align="center"
                        :max="100"
                    />
                    <small>{{ filteredProducts.length }} products</small>
                </div>
            </template>

            <!-- PREVIEW: show up to 2 standalone products -->
            <div class="collection-grid two-only">
                <div
                    v-for="p in filteredProducts.slice(0, 2)"
                    :key="`p-standalone-${p.id}`"
                    class="grid-item"
                    role="button"
                    @click.stop="openProductsModal"
                    :class="appearance.buttons"
                >
                    <div class="image-wrap">
                        <img
                            v-if="p.file"
                            :src="getProductImageSrc(p)"
                            :alt="p.title || 'Product image'"
                            loading="lazy"
                        />
                        <div v-else class="no-image-fill"></div>

                        <div class="overlay">
                            <div class="overlay-title">{{ p.title }}</div>
                            <div v-if="p.price" class="price-pill">
                                {{ p.price
                                }}<span v-if="p.currency">
                                    {{ p.currency }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ShopButton>

        <!-- POPUP: full collection -->
        <teleport to="body">
            <BootstrapModal
                v-if="selectedCollection"
                ref="modalRef"
                :modal-id="modalId"
                :title="selectedCollection?.title || 'Collection'"
                modalSize="lg"
                @close="selectedCollection = null"
            >
                <template #body>
                    <div class="shop-modal-body">
                        <div class="modal-products-grid two-cols">
                            <a
                                v-for="prod in visibleProducts"
                                :key="`modal-prod-${
                                    selectedCollection?.id || 'x'
                                }-${prod.id}`"
                                class="modal-product-card"
                                :href="
                                    prod.url || prod.link || prod.href || '#'
                                "
                                target="_blank"
                                rel="noopener noreferrer"
                                role="link"
                            >
                                <!-- Share icon button -->
                                <button
                                    class="share-top-btn"
                                    type="button"
                                    :aria-label="`Share ${prod.title || 'product'}`"
                                    title="Share"
                                    @click.stop.prevent="openShareForProduct(prod)"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <circle cx="18" cy="5" r="3" />
                                        <circle cx="6" cy="12" r="3" />
                                        <circle cx="18" cy="19" r="3" />
                                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                    </svg>
                                </button>
                                <div class="mpc-image">
                                    <img
                                        v-if="prod.file"
                                        :src="getProductImageSrc(prod)"
                                        :alt="prod.title || 'Product image'"
                                        loading="lazy"
                                    />
                                    <div v-else class="no-image-fill"></div>
                                </div>

                                <div class="mpc-info">
                                    <div class="mpc-title">
                                        {{ prod.title }}
                                    </div>
                                    <div v-if="prod.price" class="mpc-price">
                                        {{ prod.price
                                        }}<span v-if="prod.currency">
                                            {{ prod.currency }}</span
                                        >
                                    </div>
                                </div>
                            </a>
                        </div>

                        <!-- Load more controls -->
                        <div v-if="hasMore" class="load-more-wrap">
                            <button
                                class="btn btn-outline-secondary btn-sm"
                                type="button"
                                @click="loadMore"
                            >
                                Load more
                            </button>
                            <!-- Sentinel used for infinite auto-load when scrolling -->
                            <div
                                ref="sentinelRef"
                                class="infinite-sentinel"
                                aria-hidden="true"
                            ></div>
                        </div>
                    </div>
                </template>
            </BootstrapModal>
        </teleport>

        <!-- Product Share Popup -->
        <teleport to="body">
            <ShareProductPopup
                v-if="sharePopupVisible && shareProd"
                :show="sharePopupVisible"
                :url="productShareUrl(shareProd)"
                :productId="shareProd?.id"
                :title="shareProd?.title || ''"
                :file="shareProd?.file || null"
                :DoEndpoint="DoEndpoint"
                :DoBucket="DoBucket"
                :referral_code="user.referral_code"
                :user="user"
                :username="user.username"
                :urlName="urlName"
                @close="closeSharePopup"
            />
        </teleport>
    </div>
</template>

<style scoped>
/* --- Stack spacing to match other cards --- */
.shop-card-stack > :deep(.link-button) {
    margin-top: 0.5rem;
}

/* ---------- Preview (in links feed) ---------- */
.collection-grid.two-only {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    border-radius: inherit;
    overflow: hidden;
    margin-top: 0.25rem;
}
.grid-item {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #fff;
    transition: box-shadow 0.2s ease, transform 0.2s ease,
        border-color 0.2s ease;
}
.grid-item:hover {
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}
.image-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 4/5;
    background: #f5f6f7;
}
.no-image-fill {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f2f2f2, #eaeaea);
}
.image-wrap img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* small overlay for preview */
.overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 8px 10px;
    color: #fff;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
}
.overlay-title {
    font-weight: 700;
    font-size: 0.9rem;
    line-height: 1.15;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.price-pill {
    margin-top: 4px;
    display: inline-block;
    padding: 3px 8px;
    font-weight: 700;
    font-size: 0.75rem;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    border-radius: 999px;
}

/* ---------- Modal (popup) ---------- */
/* Keep modal body scrollable & compact */
.shop-modal-body {
    max-height: min(72vh, 780px);
    overflow: auto;
    padding-right: 2px; /* avoid scrollbar overlay on content */
}

/* Always 2 columns in popup; 3 on wider screens to reduce tile size */
.modal-products-grid.two-cols {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}
@media (min-width: 768px) {
    .modal-products-grid.two-cols {
        grid-template-columns: repeat(3, 1fr);
    }
}

/* Card */
.modal-product-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    text-decoration: none;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 14px;
    cursor: pointer;
    transition: box-shadow 0.2s ease, transform 0.2s ease,
        border-color 0.2s ease, background-color 0.2s ease;
    color: inherit;
    position: relative; /* for positioning share button */
}
.modal-product-card:hover {
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: rgba(0, 0, 0, 0.18);
    background: #fff;
}

/* Share icon in the top-right of each product card */
.share-top-btn{ position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border:none; border-radius:8px; background:rgba(0,0,0,.5); color:#fff; padding:0; cursor:pointer; z-index: 3; /* ensure above image */ box-shadow: 0 2px 6px rgba(0,0,0,.25); }
.share-top-btn:hover{ background:rgba(0,0,0,.6); }

/* Uniform square thumbnails in popup */
.mpc-image {
    width: 100%;
    aspect-ratio: 1 / 1; /* square to ensure same width & height */
    background: #f5f6f7;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
}
.mpc-image img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.no-image-fill {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f2f2f2, #eaeaea);
}

/* Text zone */
.mpc-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.mpc-title {
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.mpc-price {
    font-size: 0.85rem;
    opacity: 0.75;
}

/* Load more area */
.load-more-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 14px 0 6px;
}
.infinite-sentinel {
    width: 100%;
    height: 1px;
}
@media (max-width: 767.98px) {
    :deep(#shop-collection-modal-products .modal-dialog) {
        max-width: 100% !important;
        margin: 0;
    }
    :deep(#shop-collection-modal-products .modal-content) {
        height: 100vh;
        border-radius: 0;
    }
    :deep(#shop-collection-modal-products .modal-body) {
        max-height: calc(100vh - 120px);
    }
}

/* Single product preview under standalone product title */
.single-product-preview {
    width: 100%;
    margin-top: 8px;
}
</style>
